/* eslint-disable @typescript-eslint/no-explicit-any */

"use client";

import dayjs from "dayjs";
import { useRouter, useSearchParams } from "next/navigation";
import React, { useState, useCallback, useMemo } from "react";
import { DateInput, TextFieldSts } from "staysure-component-library";

import {
  QuestionAreaProps,
  SecurityQuestions,
} from "@/app/common-types/quote/policy-document-types";
import LabelButtonWrapper from "@/components/atoms/button-wrapper/button-wrapper";
import { useSubmitPolicyDocData } from "@/hooks/api/policy-document/submit-policy-doc-data";
import useMediaQuery from "@/hooks/use-media-query/use-media-query";
import { useLoading } from "@/providers/loading-provider";
import { trimFormValue } from "@/utils/form-input.utils";
import {
  getDobValidation,
  getDobValidationState,
} from "@/utils/policy-document.utils";
import { isValidDate } from "@/utils/travel-date.utils";

import Styles from "./form-content.module.css";
import PolicyDocumentModal from "../policy-document-modal/policy-document-modal";

function FormContent({
  questionTitle,
  postCodeLabel = "Postcode",
  postCodeInvalid = "Please enter a valid postcode",
  phoneNumberLabel = "Last 5 digits of telephone",
  phoneNumberInvalidMsg = "Invalid telephone number",
  dobLabel,
  dobMaxAge,
  dobMinAge,
  dobInvalidAge,
  btnLabel,
  popupTitle,
  popupContent,
  popupBtnLabel,
  popupContentUnauthorized,
  setPolicyDocData,
  showPostCode = true,
  showPhoneNumber = false,
  postCodeMinLength = 4,
  postCodeMaxLength = 8,
  postCodeRegex = "^[A-Za-z0-9 ]{4,8}$",
  phoneNumberLength = 5,
  phoneNumberRegex = "^[0-9]{5}$",
}: Readonly<
  QuestionAreaProps & {
    setPolicyDocData?: (data: any) => void;
  }
>) {
  const isDesktop = useMediaQuery("(min-width: 600px)");
  const router = useRouter();
  const searchParams = useSearchParams();
  const policyId = searchParams.get("policyId");
  const { showLoading, hideLoading } = useLoading();

  const [formData, setFormData] = useState({
    postCode: "",
    phoneNumber: "",
    dateValue: "",
    age: "",
    invalidDate: "",
  });
  const [postCodeError, setPostCodeError] = useState<string>("");
  const [phoneNumberError, setPhoneNumberError] = useState<string>("");
  const [policyDocumentModalOpen, setPolicyDocumentModalOpen] = useState(false);
  const [unauthorizedModalOpen, setUnauthorizedModalOpen] = useState(false);

  const submitPolicyDocData = useSubmitPolicyDocData(policyId);

  const handleInputChange = useCallback((date: Date) => {
    if (isValidDate(date) && date) {
      const currentDate = dayjs();
      const birthDate = dayjs(date);
      const currentAge = dayjs().diff(birthDate, "year");
      const formattedDate = dayjs(date, "DD/MM/YYYY").toString();

      // Check if the birth date is in the future
      const isFutureDate = birthDate.isAfter(currentDate);
      let ageValue = 0;
      if (isFutureDate) {
        if (currentAge === 0) {
          ageValue = -0.5;
        } else if (currentAge < 0) {
          ageValue = currentAge;
        }
      } else {
        ageValue = currentAge;
      }
      setFormData((prev) => ({
        ...prev,
        dateValue: formattedDate,
        age: ageValue.toString(),
        invalidDate: "",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        dateValue: "",
        age: "",
        invalidDate: "error",
      }));
    }
  }, []);

  const handlePostCodeChange = useCallback(
    (value: string) => {
      const regex = new RegExp(postCodeRegex);
      const trimmedValue = value.replace(/\s+/g, " ").trim();
      const isInvalidPostcode =
        trimmedValue?.length > 0 && !regex.test(trimmedValue);

      setPostCodeError(isInvalidPostcode ? postCodeInvalid : "");
      setFormData((prev) => ({ ...prev, postCode: trimmedValue }));
    },
    [postCodeInvalid, postCodeRegex]
  );

  const handlePhoneNumberChange = useCallback(
    (value: string) => {
      const regex = new RegExp(phoneNumberRegex);
      const isInvalid = value?.length > 0 && !regex.test(value);

      setPhoneNumberError(isInvalid ? phoneNumberInvalidMsg : "");
      setFormData((prev) => ({ ...prev, phoneNumber: value }));
    },
    [phoneNumberInvalidMsg, phoneNumberRegex]
  );

  const handleSubmit = useCallback(async () => {
    showLoading();

    const formattedDate = formData.dateValue
      ? dayjs(formData.dateValue).format("DD/MM/YYYY")
      : "";

    const payloadBase: Partial<SecurityQuestions> = {
      "2": formattedDate,
    };

    const phoneNumber = trimFormValue(formData.phoneNumber);

    // Add fields based on what's enabled
    if (showPostCode && formData.postCode) {
      payloadBase["1"] = formData.postCode;
    }

    // bff doesn't accept both the postcode and phone number, so we have to pass the phone number to the same slot as the postcode
    if (showPhoneNumber && phoneNumber) {
      payloadBase["1"] = phoneNumber;
    }

    const response = await submitPolicyDocData(
      payloadBase as SecurityQuestions
    );

    if (response?.data?.attributes?.securityQuestionResponse?.valid) {
      hideLoading();
      setPolicyDocData?.(
        response?.data?.attributes?.securityQuestionResponse?.documentLinks
      );
      const { pathname } = window.location;
      const urlWithoutParams = window.location.origin + pathname;
      router.replace(urlWithoutParams);
    } else {
      if (response === 401) {
        setUnauthorizedModalOpen(true);
      } else {
        setPolicyDocumentModalOpen(true);
      }
      hideLoading();
    }
  }, [
    formData,
    showPostCode,
    showPhoneNumber,
    submitPolicyDocData,
    setPolicyDocData,
    router,
    showLoading,
    hideLoading,
  ]);

  const isFormInvalid = useMemo(() => {
    let baseValidation =
      !formData.dateValue ||
      getDobValidationState(formData.dateValue, formData.age);

    // Add postcode validation if enabled
    if (showPostCode) {
      baseValidation = baseValidation || !formData.postCode || !!postCodeError;
    }

    // Add phone number validation if enabled
    if (showPhoneNumber) {
      baseValidation =
        baseValidation || !formData.phoneNumber || !!phoneNumberError;
    }

    return baseValidation;
  }, [
    formData,
    postCodeError,
    phoneNumberError,
    showPostCode,
    showPhoneNumber,
  ]);

  return (
    <div
      data-testid="formContent-testid"
      className={`${Styles.form_content} flex flex-col gap-spacing-m md:gap-spacing-l`}
    >
      <PolicyDocumentModal
        isOpen={policyDocumentModalOpen}
        setIsOpen={setPolicyDocumentModalOpen}
        content={{
          popupTitle,
          popupContent,
          popupBtnLabel,
        }}
      />

      <PolicyDocumentModal
        isOpen={unauthorizedModalOpen}
        setIsOpen={setUnauthorizedModalOpen}
        content={{
          popupTitle,
          popupContentUnauthorized,
          popupBtnLabel,
        }}
      />

      <div data-testid="form-content" className="text-xs leading-[27px]">
        {questionTitle ??
          "Please answer the following questions to proceed to the next screen to view your policy documentation:"}
      </div>
      <div
        className={`${Styles.postcode_block} flex flex-col gap-spacing-l w-[264px] md:w-[327px]`}
      >
        {/* postcode field */}
        {showPostCode && (
          <div className="postcode-wrapper">
            <TextFieldSts
              label={postCodeLabel ?? "Postcode"}
              name="postCode"
              id="postCode"
              width="100%"
              maxLength={postCodeMaxLength}
              minLength={postCodeMinLength}
              labelLeftPadding="16px"
              onChange={handlePostCodeChange}
              aria-label="postcode"
              state={postCodeError ? "error" : undefined}
              message={postCodeError}
              value={formData.postCode}
            />
          </div>
        )}

        {/* phone number field */}
        {showPhoneNumber && (
          <div className="phone-number-wrapper">
            <TextFieldSts
              label={phoneNumberLabel}
              name="phoneNumber"
              id="phoneNumber"
              width="100%"
              maxLength={phoneNumberLength}
              minLength={phoneNumberLength}
              labelLeftPadding="16px"
              onChange={handlePhoneNumberChange}
              aria-label="phone number"
              state={phoneNumberError ? "error" : undefined}
              message={phoneNumberError}
              value={formData.phoneNumber}
            />
          </div>
        )}

        <div
          className={`${Styles.date_picker_wrapper} flex flex-col relative mb-spacing-s`}
        >
          <DateInput
            isDOBScrollable={!isDesktop}
            defaultDate={
              formData.dateValue &&
              isValidDate(formData.dateValue as unknown as Date)
                ? new Date(formData.dateValue)
                : undefined
            }
            hintLabel={dobLabel ?? "DOB"}
            brand="staysure"
            dobErrorMsgClass={Styles.errorOfDob}
            dobWrapperClass={`${Styles.dobWrapper}`}
            isError={getDobValidationState(formData.dateValue, formData.age)}
            customizedMessage={
              getDobValidation(
                formData.age,
                formData.dateValue,
                dobMinAge,
                dobInvalidAge,
                dobMaxAge
              ) || formData.invalidDate
            }
            onChange={(e: Date) => handleInputChange(e as Date)}
          />
        </div>
      </div>
      <LabelButtonWrapper
        isDisabled={isFormInvalid}
        data-testid="submit-form-button"
        id="submit-form-button"
        label={btnLabel ?? "Submit"}
        hierarchy="primary"
        className="max-h-spacing-xxl max-w-[111px] text-[18px] leading-[100%] font-[600] font-body-font"
        type="button"
        onClick={handleSubmit}
      />
    </div>
  );
}

export default FormContent;
